import React, { useCallback, useEffect, useRef } from 'react';
import { ShapesDrawingToolsDndIcon } from '@icons';

import './AnnotationShortcutHint.less';

const POSITION_KEY = 'annotation-shortcut-hint-position';
const BOUNDS_SELECTOR = '.media_viewer_video.media_viewer_asset';

function getBounds() {
  return document.querySelector(BOUNDS_SELECTOR)?.getBoundingClientRect();
}

function parseTranslate(transform: string) {
  const match = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/);
  if (!match) return null;
  return { x: parseFloat(match[1]), y: parseFloat(match[2]) };
}

function AnnotationShortcutHint() {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMouseDownRef = useRef(false);
  const dragOffsetRef = useRef({ left: 0, top: 0 });

  const clampTranslate = useCallback((x: number, y: number) => {
    const el = containerRef.current;
    if (!el) return { x, y };

    const bounds = getBounds();
    if (!bounds) return { x, y };

    const { left, top } = dragOffsetRef.current;
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    return {
      x: Math.min(Math.max(x, bounds.left - left), bounds.right - width - left),
      y: Math.min(Math.max(y, bounds.top - top), bounds.bottom - height - top)
    };
  }, []);

  const persistPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el?.style.transform) return;
    localStorage.setItem(
      POSITION_KEY,
      JSON.stringify({
        left: el.style.left,
        top: el.style.top,
        transform: el.style.transform
      })
    );
  }, []);

  const reclampToBounds = useCallback(() => {
    const el = containerRef.current;
    if (!el || el.style.position !== 'fixed') return;

    const bounds = getBounds();
    if (!bounds) return;

    const rect = el.getBoundingClientRect();
    const maxLeft = Math.max(bounds.left, bounds.right - rect.width);
    const maxTop = Math.max(bounds.top, bounds.bottom - rect.height);
    const nextLeft = Math.min(Math.max(rect.left, bounds.left), maxLeft);
    const nextTop = Math.min(Math.max(rect.top, bounds.top), maxTop);
    const dx = nextLeft - rect.left;
    const dy = nextTop - rect.top;
    if (dx === 0 && dy === 0) return;

    const current = parseTranslate(el.style.transform);
    if (!current) return;

    el.style.transform = `translate(${current.x + dx}px, ${current.y + dy}px)`;
    persistPosition();
  }, [persistPosition]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const raw = localStorage.getItem(POSITION_KEY);
    if (!raw) return;
    try {
      const { left, top, transform } = JSON.parse(raw);
      el.style.position = 'fixed';
      el.style.top = top;
      el.style.left = left;
      el.style.right = 'unset';
      el.style.bottom = 'unset';
      el.style.transform = transform;
      el.style.zIndex = '100';
      dragOffsetRef.current = {
        left: parseFloat(left) || 0,
        top: parseFloat(top) || 0
      };
      reclampToBounds();
    } catch {
      // eslint-disable-next-line no-empty
    }
  }, [reclampToBounds]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isMouseDownRef.current || !containerRef.current) return;
      const skip = e.pageX === 0 && e.pageY === 0;
      if (!skip) {
        const { x, y } = clampTranslate(e.clientX, e.clientY);
        containerRef.current.style.transform = `translate(${x}px, ${y}px)`;
      }
    };

    const onMouseUp = () => {
      if (!isMouseDownRef.current) return;
      isMouseDownRef.current = false;
      persistPosition();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [clampTranslate, persistPosition]);

  useEffect(() => {
    const boundsEl = document.querySelector(BOUNDS_SELECTOR);
    if (!boundsEl) return;

    const observer = new ResizeObserver(() => {
      reclampToBounds();
    });
    observer.observe(boundsEl);
    window.addEventListener('resize', reclampToBounds);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', reclampToBounds);
    };
  }, [reclampToBounds]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current) return;
      isMouseDownRef.current = true;
      const rect = containerRef.current.getBoundingClientRect();
      const left = rect.x - e.clientX;
      const top = rect.y - e.clientY;
      dragOffsetRef.current = { left, top };
      containerRef.current.style.position = 'fixed';
      containerRef.current.style.top = `${top}px`;
      containerRef.current.style.right = 'unset';
      containerRef.current.style.left = `${left}px`;
      containerRef.current.style.bottom = 'unset';
      const { x, y } = clampTranslate(e.clientX, e.clientY);
      containerRef.current.style.transform = `translate(${x}px, ${y}px)`;
      containerRef.current.style.zIndex = '100';
    },
    [clampTranslate]
  );

  return (
    <div
      ref={containerRef}
      className="annotation_shortcut_hint"
      onMouseDown={onMouseDown}
    >
      <div className="annotation_shortcut_hint__dragger">
        <ShapesDrawingToolsDndIcon />
      </div>
      <div className="annotation_shortcut_hint__text">
        <div className="annotation_shortcut_hint__key">Space</div> Play/Pause{' '}
      </div>
      <div className="annotation_shortcut_hint__text">
        <div className="annotation_shortcut_hint__key">D</div> Annotate
      </div>
      <div className="annotation_shortcut_hint__text">
        <div className="annotation_shortcut_hint__key">C</div> Comment
      </div>
    </div>
  );
}

export default AnnotationShortcutHint;
